import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Sección de acertijo dentro del modal del entreno.
 * - Cliente apuntado: ve el enunciado y puede responder (1 vez) si está abierto.
 * - Entrenador: ve tabla completa (acertaron/fallaron/sin responder/sospechosos) y acciones.
 */
export default function AcertijoSection({ training, isEntrenador, onChanged }) {
  const [acertijo, setAcertijo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [respuesta, setRespuesta] = useState('');
  const [sending, setSending] = useState(false);

  const fetchAcertijo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trainings/${training.id}/acertijo`);
      setAcertijo(res.data);
    } catch (e) {
      setAcertijo(null);
    } finally {
      setLoading(false);
    }
  }, [training.id]);

  useEffect(() => { fetchAcertijo(); }, [fetchAcertijo]);

  if (loading) return null;
  if (!acertijo) {
    if (isEntrenador) {
      return null; // El entrenador crea el reto al crear/editar el entreno
    }
    return null;
  }

  // ============== VISTA CLIENTE ==============
  if (!isEntrenador) {
    const cerrado = acertijo.estado === 'cerrado';
    const yaRespondi = acertijo.ya_respondi;

    const handleResponder = async (e) => {
      e.preventDefault();
      if (!respuesta.trim()) return;
      if (!window.confirm(`¿Confirmas tu respuesta: "${respuesta.trim()}"? Solo tienes 1 intento.`)) return;
      setSending(true);
      try {
        await axios.post(`${API}/trainings/${training.id}/acertijo/responder`, {
          respuesta: respuesta.trim()
        });
        await fetchAcertijo();
        if (onChanged) onChanged();
      } catch (err) {
        alert(err.response?.data?.detail || 'Error al enviar respuesta');
      } finally {
        setSending(false);
      }
    };

    return (
      <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-3 space-y-2" data-testid="acertijo-cliente">
        <div className="flex items-center gap-2 text-purple-300 font-bold text-sm">
          🎮 Reto de este entreno
        </div>
        <p className="text-white text-sm">{acertijo.enunciado}</p>
        <p className="text-[11px] text-gray-400">Consecuencia por fallar: <span className="text-orange-300 font-semibold">{acertijo.consecuencia || 'a confirmar'}</span></p>

        {/* Aviso anti-trampas */}
        <div className="bg-red-900/30 border border-red-700/50 rounded p-2 text-[11px] text-red-200 leading-snug" data-testid="acertijo-aviso">
          ⚠️ <span className="font-bold">El reto es personal.</span> Resuélvelo tú solo, sin ayuda externa ni de compañeros. Si detectamos respuestas iguales desde la misma red o casi al mismo tiempo, quedarás marcado como sospechoso y la consecuencia será doble 🚨
        </div>

        {cerrado ? (
          <div className="text-center bg-gray-800/60 rounded p-2 mt-2">
            <p className="text-xs text-gray-300">El reto está cerrado.</p>
            {yaRespondi && (
              <p className="text-sm font-bold mt-1 text-purple-300" data-testid="acertijo-resultado">
                🎲 La suerte está echada
              </p>
            )}
          </div>
        ) : yaRespondi ? (
          <div className="text-center bg-green-900/30 border border-green-700/50 rounded p-2 mt-2">
            <p className="text-xs text-green-300">✓ Has respondido: <span className="font-semibold">{acertijo.mi_respuesta}</span></p>
            <p className="text-[11px] text-purple-300 mt-1 font-semibold">🎲 La suerte está echada</p>
          </div>
        ) : (
          <form onSubmit={handleResponder} className="space-y-2 pt-1">
            <Input
              value={respuesta}
              onChange={(e) => setRespuesta(e.target.value)}
              placeholder="Tu respuesta..."
              className="bg-gray-900 border-purple-700/50 text-white"
              disabled={sending}
              data-testid="input-respuesta-acertijo"
            />
            <Button
              type="submit"
              disabled={sending || !respuesta.trim()}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm"
              data-testid="btn-enviar-respuesta-acertijo"
            >
              {sending ? 'Enviando...' : 'Enviar respuesta (1 sola oportunidad)'}
            </Button>
            <p className="text-[10px] text-gray-500 text-center">Se cierra 2 horas antes del entreno.</p>
          </form>
        )}
      </div>
    );
  }

  // ============== VISTA ENTRENADOR ==============
  const respuestas = acertijo.respuestas || [];
  const acertaron = respuestas.filter(r => r.correcta);
  const fallaron = respuestas.filter(r => !r.correcta && !r.no_respondio);
  const noRespondidos = (acertijo.sin_responder || []).concat(respuestas.filter(r => r.no_respondio).map(r => ({ user_id: r.user_id, nombre: r.user_nombre || 'Usuario' })));
  const sospechosos = respuestas.filter(r => r.sospechoso_auto || r.sospechoso_manual);

  const handleRecordar = async () => {
    try {
      const res = await axios.post(`${API}/trainings/${training.id}/acertijo/recordar`);
      alert(`Recordatorio enviado a ${res.data.enviados} usuario(s)`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleToggleSospechoso = async (uid) => {
    try {
      await axios.post(`${API}/trainings/${training.id}/acertijo/marcar-sospechoso/${uid}`);
      await fetchAcertijo();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleToggleCorrecta = async (uid) => {
    try {
      await axios.post(`${API}/trainings/${training.id}/acertijo/marcar-correcta/${uid}`);
      await fetchAcertijo();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleReevaluar = async () => {
    if (!window.confirm('¿Re-evaluar todas las respuestas con la lógica actual? (corrige fallos por puntuación o artículos)')) return;
    try {
      const res = await axios.post(`${API}/trainings/${training.id}/acertijo/reevaluar`);
      alert(`${res.data.cambios} respuesta(s) cambiaron de estado`);
      await fetchAcertijo();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleRedetectarSospechosos = async () => {
    if (!window.confirm('¿Re-analizar todas las respuestas para detectar sospechosos? (mismo IP + respuesta < 5 min)')) return;
    try {
      const res = await axios.post(`${API}/trainings/${training.id}/acertijo/redetectar-sospechosos`);
      alert(`${res.data.cambios} usuario(s) marcado(s) como sospechosos`);
      await fetchAcertijo();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleEliminarAcertijo = async () => {
    if (!window.confirm('¿Eliminar este reto? Se perderán todas las respuestas.')) return;
    try {
      await axios.delete(`${API}/trainings/${training.id}/acertijo`);
      await fetchAcertijo();
      if (onChanged) onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-3 space-y-2" data-testid="acertijo-entrenador">
      <div className="flex items-center justify-between">
        <div className="text-purple-300 font-bold text-sm flex items-center gap-2">
          🎮 Reto: {acertijo.estado === 'cerrado' ? '🔒 Cerrado' : '🟢 Abierto'}
        </div>
        <button onClick={handleEliminarAcertijo} className="text-red-400 text-xs hover:underline" data-testid="btn-borrar-acertijo">Eliminar</button>
      </div>
      <p className="text-white text-sm">{acertijo.enunciado}</p>
      <p className="text-[11px] text-gray-300">Respuesta: <span className="text-green-400 font-semibold">{acertijo.respuesta_correcta}</span> · Consecuencia: <span className="text-orange-300">{acertijo.consecuencia}</span></p>

      <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
        <div className="bg-green-900/30 rounded p-2">
          <div className="font-bold text-green-400">✅ Acertaron ({acertaron.length})</div>
          <ul className="text-gray-200 mt-1 space-y-1">
            {acertaron.map(r => (
              <li key={r.user_id} className="flex items-start justify-between gap-1">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{r.user_nombre}</div>
                  <div className="text-[10px] text-gray-400 break-words whitespace-pre-wrap">«{r.respuesta}»</div>
                </div>
                <button
                  onClick={() => handleToggleSospechoso(r.user_id)}
                  className={`px-1 rounded text-[9px] flex-shrink-0 ${r.sospechoso_manual || r.sospechoso_auto ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-400 hover:bg-yellow-700'}`}
                  title={r.sospechoso_auto ? 'Sospechoso automático' : 'Marcar sospechoso'}
                  data-testid={`btn-sospechoso-${r.user_id}`}
                >
                  {r.sospechoso_manual || r.sospechoso_auto ? '🚨' : '?'}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-red-900/30 rounded p-2">
          <div className="font-bold text-red-400">❌ Fallaron ({fallaron.length})</div>
          <ul className="text-gray-200 mt-1 space-y-1">
            {fallaron.map(r => (
              <li key={r.user_id} className="flex items-start justify-between gap-1">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{r.user_nombre}</div>
                  <div className="text-[10px] text-gray-400 break-words whitespace-pre-wrap">«{r.respuesta}»</div>
                </div>
                <button
                  onClick={() => handleToggleCorrecta(r.user_id)}
                  className="bg-green-700/40 hover:bg-green-600 text-green-200 text-[9px] px-1.5 py-0.5 rounded font-bold flex-shrink-0"
                  title="Dar por correcta"
                  data-testid={`btn-marcar-correcta-${r.user_id}`}
                >
                  ✓
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-gray-800 rounded p-2">
          <div className="font-bold text-gray-300">⏳ Sin responder ({noRespondidos.length})</div>
          <ul className="text-gray-200 mt-1 space-y-0.5">
            {noRespondidos.map(u => <li key={u.user_id}>{u.nombre}</li>)}
          </ul>
        </div>
        <div className="bg-yellow-900/30 rounded p-2">
          <div className="font-bold text-yellow-400">🚨 Sospechosos ({sospechosos.length})</div>
          <ul className="text-gray-200 mt-1 space-y-1">
            {sospechosos.map(r => (
              <li key={r.user_id}>
                <div className="flex items-center gap-1">
                  <span className="font-medium">{r.user_nombre}</span>
                  {r.sospechoso_auto && <span className="text-[9px] bg-yellow-700 px-1 rounded">auto</span>}
                  {r.sospechoso_manual && <span className="text-[9px] bg-orange-700 px-1 rounded">manual</span>}
                </div>
                {r.sospechoso_motivo && (
                  <div className="text-[10px] text-yellow-300/80 italic mt-0.5" data-testid={`motivo-${r.user_id}`}>
                    ↳ {r.sospechoso_motivo}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {acertijo.estado === 'abierto' && noRespondidos.length > 0 && (
        <Button
          onClick={handleRecordar}
          className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white text-xs h-8"
          data-testid="btn-recordar-acertijo"
        >
          📨 Recordar a los que faltan ({noRespondidos.length})
        </Button>
      )}

      <button
        onClick={handleReevaluar}
        className="w-full mt-1 text-[10px] text-gray-400 hover:text-gray-200 underline"
        data-testid="btn-reevaluar-acertijo"
      >
        🔄 Re-evaluar respuestas (corrige fallos por puntuación / artículos)
      </button>
      <button
        onClick={handleRedetectarSospechosos}
        className="w-full text-[10px] text-gray-400 hover:text-yellow-300 underline"
        data-testid="btn-redetectar-sospechosos"
      >
        🚨 Re-analizar sospechosos (misma IP + &lt; 5 min)
      </button>
    </div>
  );
}
